import { validateAmounts } from "../utils";
import {
  EuerDeclarationCarUsageSubform1PctMethodInputs,
  EuerDeclarationCarUsageSubformCalculationResults,
} from "./types";
import {
  getMinDeductibleTravelExpenses,
  getReducedGrossPrice,
  roundDownCentsToFullHundreds,
  VAT_RATE,
} from "./utils";

const FIXED_FLAT_RATE_DEDUCTION = 0.8;
const TRIPS_FIXED_RATE = 0.0003;
const FIXED_PRIVATE_USAGE_RATE = 0.01;

const validate1PctAmounts = (
  inputs: EuerDeclarationCarUsageSubform1PctMethodInputs
): void => {
  // Ignoring the following rule as we are destructing the object to omit the unused values.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { purchaseDate, carType, ...numberValues } = inputs;

  const amounts = Object.values(numberValues);
  validateAmounts(amounts);
};

/**
 * Updates `privateCarUsage` & `homeCompanyTrip` values based on `costCap`.
 * When costCap is negative we need to subtract it from `homeCompanyTrips`
 * and the remaining from `privateCarUsage`.
 */
const adjustPrivateCarUsageAndHomeCompanyTrips = ({
  privateCarUsage,
  homeCompanyTrips,
  costCap,
}: {
  privateCarUsage: number;
  homeCompanyTrips: number;
  costCap: number;
}): {
  privateCarUsage: number;
  homeCompanyTrips: number;
} => {
  if (costCap < 0) {
    const activeCostCap = Math.abs(costCap);
    // If `activeCostCap` doesn't exceed `homeCompanyTrips`, subtract everything from it.
    // Else make `homeCompanyTrips` 0 (it shouldn't become negative) and subtract the remnant from `privateCarUsage`.
    if (activeCostCap <= homeCompanyTrips) {
      homeCompanyTrips -= activeCostCap;
    } else {
      const subtractionRemnant = activeCostCap - homeCompanyTrips;
      homeCompanyTrips = 0;
      privateCarUsage -= subtractionRemnant;
    }
  }

  return { privateCarUsage, homeCompanyTrips };
};

export const calculateCarUsageSubform1PctMethodResults = (
  inputs: EuerDeclarationCarUsageSubform1PctMethodInputs,
  declarationYear: number
): EuerDeclarationCarUsageSubformCalculationResults => {
  if (declarationYear <= 0) {
    throw new Error("Invalid declaration year");
  }
  validate1PctAmounts(inputs);

  const {
    actualCosts,
    catalogPrice,
    carType,
    collectedVat: collectedVatInput,
    commuteDistance,
    commuteDays,
    depreciationCosts,
    interestCosts,
    usageMonths,
  } = inputs;
  const reducedGrossPrice = getReducedGrossPrice({
    grossPriceInCents: catalogPrice,
    carType,
  });

  const roundedGrossPrice = roundDownCentsToFullHundreds(catalogPrice);

  // [106] Private Kfz-Nutzung
  let privateCarUsage = Math.round(
    reducedGrossPrice * FIXED_PRIVATE_USAGE_RATE * usageMonths
  );
  // [140] Vereinnahmte USt 19 % (Calculation is similar to [106] but we used only rounded price, not reduced).
  const collectedVatCalculation = Math.round(
    roundedGrossPrice *
      FIXED_PRIVATE_USAGE_RATE *
      usageMonths *
      FIXED_FLAT_RATE_DEDUCTION *
      VAT_RATE
  );
  // [142] Fahrten zwischen Wohnung/Betrieb
  let homeCompanyTrips = Math.round(
    reducedGrossPrice * TRIPS_FIXED_RATE * usageMonths * commuteDistance
  );
  // [176] Min. abziehbare Fahrtkosten
  const minDeductibleTravelExpenses = getMinDeductibleTravelExpenses({
    commuteDays,
    commuteDistance,
    declarationYear,
  });

  const costCap =
    actualCosts +
    interestCosts +
    depreciationCosts -
    (privateCarUsage + homeCompanyTrips);

  ({ privateCarUsage, homeCompanyTrips } =
    adjustPrivateCarUsageAndHomeCompanyTrips({
      privateCarUsage,
      homeCompanyTrips,
      costCap,
    }));

  // When user provides a manual `collectedVat` we use it instead of the calculated value, but we still
  // return both as part of the result for informational purposes.
  const collectedVat = collectedVatInput ?? collectedVatCalculation;

  // Gewinnerhöhung = [106] + [140] + [142] - [176]
  const profitIncrease =
    privateCarUsage +
    collectedVat +
    homeCompanyTrips -
    minDeductibleTravelExpenses;

  return {
    privateCarUsage,
    collectedVat,
    autoCalculatedCollectedVat: collectedVatCalculation,
    homeCompanyTrips,
    minDeductibleTravelExpenses,
    costCap,
    profitIncrease,
  };
};
